import { useApp } from '@/contexts/AppContext';
import { format } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Bell, Check, Trash2, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotificationsSheetProps {
  unreadCount: number;
}

export function NotificationsSheet({ unreadCount }: NotificationsSheetProps) {
  const {
    notifications,
    unreadNotificationsCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
  } = useApp();

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'task':
        return '📋';
      case 'reminder':
        return '⏰';
      case 'achievement':
        return '🏆';
      default:
        return 'ℹ️';
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full hover:bg-gray-100"
        >
          <Bell className="h-5 w-5 text-gray-600" />
          {unreadCount > 0 && (
            <span className={cn(
              "absolute top-0 right-0 h-4 min-w-[16px] rounded-full bg-red-500 text-[10px] font-bold text-white",
              "flex items-center justify-center px-1",
              "border-2 border-background",
              unreadCount > 99 ? "px-1.5" : ""
            )}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent 
        side="top" 
        className={cn(
          "w-full sm:max-w-lg sm:mx-auto p-0",
          "border-0 sm:border rounded-none sm:rounded-lg mt-0",
          "bg-white/95 backdrop-blur-xl",
          "fixed top-0 left-0 right-0"
        )}
      >
        <div className="flex flex-col h-[85vh] sm:h-auto max-h-[85vh]">
          {/* Swipe indicator at top */}
          <div className="sm:hidden px-4 pt-2 pb-1">
            <div className="flex flex-col items-center">
              <div className="w-12 h-1 rounded-full bg-gray-300 mb-1" />
              <ChevronDown className="w-4 h-4 text-gray-400 animate-bounce" />
            </div>
          </div>

          <SheetHeader className="px-4 py-3 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-lg">Notifications</SheetTitle>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllNotificationsAsRead}
                    className="text-xs h-8"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark all as read
                  </Button>
                )}
                <SheetClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 rounded-lg transition-colors",
                    "border border-gray-100",
                    !notification.read ? "bg-gray-50" : "bg-white"
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <span className="text-xl" role="img" aria-label={notification.type}>
                        {getNotificationIcon(notification.type)}
                      </span>
                      <div>
                        <h4 className={cn(
                          "text-sm font-medium",
                          !notification.read && "font-semibold"
                        )}>
                          {notification.title}
                        </h4>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {format(new Date(notification.createdAt), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => markNotificationAsRead(notification.id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => deleteNotification(notification.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
} 